import { Injectable, ComponentRef, ViewContainerRef, ComponentFactoryResolver, ApplicationRef, Injector, EmbeddedViewRef } from '@angular/core';
import { ReleaseNotesDialogComponent } from './release-notes-dialog.component';
import { ReleaseNotesService, ZoneFeature } from './release-notes.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ReleaseNotesDialogService {
  private componentRef: ComponentRef<ReleaseNotesDialogComponent> | null = null;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
    private releaseNotesService: ReleaseNotesService
  ) {}

  /**
   * Show the release notes dialog with the provided features
   * @param features Array of features to display
   */
  public showDialog(features: ZoneFeature[]): void {
    if (this.componentRef) {
      // Dialog is already open
      return;
    }

    // Create component
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ReleaseNotesDialogComponent);
    this.componentRef = componentFactory.create(this.injector);

    // Set inputs
    this.componentRef.instance.features = features;
    this.componentRef.instance.visible = true;

    // Subscribe to close event
    this.componentRef.instance.close.subscribe(() => {
      this.closeDialog();
    });

    // Attach to DOM
    this.appRef.attachView(this.componentRef.hostView);
    const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
  }

  /**
   * Show release notes dialog for a specific zone
   * Automatically fetches and displays new features, then marks them as seen
   * @param zone The zone identifier (e.g., "CONNECT")
   * @returns Observable that completes when features are fetched
   */
  public showDialogForZone(zone: string): Observable<ZoneFeature[]> {
    return this.releaseNotesService.getNewZoneFeatures(zone).pipe(
      tap((features) => {
        if (features && features.length > 0) {
          this.showDialog(features);
          // Mark features as seen after showing the dialog
          this.releaseNotesService.markZoneFeaturesAsSeen(zone).subscribe();
        }
      })
    );
  }

  /**
   * Check if there are new features for a zone and show dialog if there are
   * This is the main method to call from ngOnInit
   * @param zone The zone identifier (e.g., "CONNECT")
   * @param onlyIfNewFeatures If true, dialog will only show if there are new features (default: true)
   * @returns Observable<boolean> indicating if dialog was shown
   */
  public checkAndShowReleaseNotes(zone: string, onlyIfNewFeatures: boolean = true): Observable<boolean> {
    return new Observable((observer) => {
      this.releaseNotesService.getNewZoneFeatures(zone).subscribe({
        next: (features) => {
          if (features && features.length > 0) {
            this.showDialog(features);
            // Mark features as seen
            this.releaseNotesService.markZoneFeaturesAsSeen(zone).subscribe();
            observer.next(true);
          } else if (!onlyIfNewFeatures) {
            // If you want to show an empty dialog even when there are no features
            this.showDialog([]);
            observer.next(true);
          } else {
            observer.next(false);
          }
          observer.complete();
        },
        error: (error) => {
          console.error('Error checking release notes:', error);
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Close the dialog and clean up
   */
  private closeDialog(): void {
    if (!this.componentRef) {
      return;
    }

    this.appRef.detachView(this.componentRef.hostView);
    this.componentRef.destroy();
    this.componentRef = null;
  }

  /**
   * Check if dialog is currently open
   */
  public isDialogOpen(): boolean {
    return this.componentRef !== null;
  }

  /**
   * Force close the dialog if it's open
   */
  public forceCloseDialog(): void {
    this.closeDialog();
  }
}